import { useId, useState, type FormEvent } from 'react';
import { CheckCircle2, Loader2, Mail } from 'lucide-react';
import clsx from 'clsx';
import { TEXTO_CONSENTIMENTO_INTERESSE, type OrigemInteresse } from '@social/shared';
import { useInteresse } from '../hooks/use-interesse';

interface Props {
  /** De onde a captura foi disparada — grava em `interessado.origem`. */
  origem: OrigemInteresse;
  className?: string;
}

/** Minutos arredondados para cima, mesmo critério de `erro-auditoria.tsx`. */
function emMinutos(segundos: number): string {
  const minutos = Math.max(1, Math.ceil(segundos / 60));
  return minutos === 1 ? '1 minuto' : `${minutos} minutos`;
}

/**
 * Captura de e-mail do funil (`POST /api/interesse`) — reusada nos três
 * pontos da landing (depois do laudo da auditoria, nos planos, no CTA final),
 * cada um passando a própria `origem`. Ver
 * `docs/superpowers/specs/2026-08-26-specializer-social-design.md`.
 *
 * Isto é CONSENTIMENTO, não execução de contrato (LGPD art. 7º, I e art. 8º):
 * o checkbox nasce desmarcado — nunca pré-marcado — e o texto do que se
 * aceita fica visível aqui do lado, não atrás de um link. O botão fica
 * desabilitado até o checkbox ser marcado, então não existe caminho de
 * mandar o formulário sem consentir.
 *
 * Não promete o que a API não faz: nenhum e-mail é disparado hoje, então a
 * mensagem de sucesso fala em "avisar", nunca em "enviar o laudo por
 * e-mail". A saída (`POST /api/interesse/sair`) ainda não tem tela própria —
 * o link de contato abaixo é a porta que existe desde o primeiro dia.
 */
export function CapturaInteresse({ origem, className }: Props): JSX.Element {
  const id = useId();
  const { estado, registrar } = useInteresse();
  const [email, setEmail] = useState('');
  const [aceite, setAceite] = useState(false);

  async function enviar(evento: FormEvent): Promise<void> {
    evento.preventDefault();
    await registrar(email, origem, aceite);
  }

  if (estado.fase === 'sucesso') {
    return (
      <p
        role="status"
        className={clsx(
          'text-auth-texto flex items-center justify-center gap-2 text-sm font-medium',
          className,
        )}
      >
        <CheckCircle2 size={16} className="text-auth-ok shrink-0" aria-hidden />
        Prontinho — vamos te avisar por e-mail.
      </p>
    );
  }

  const enviando = estado.fase === 'enviando';
  const erroDeEmail =
    estado.fase === 'erro' && estado.campo === 'email' ? estado.mensagem : undefined;
  const erroGeral =
    estado.fase === 'erro' && estado.campo !== 'email' ? estado : undefined;

  return (
    <form onSubmit={(e) => void enviar(e)} className={className} noValidate>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
        <div className="flex-1">
          <label htmlFor={`${id}-email`} className="sr-only">
            E-mail
          </label>
          <input
            id={`${id}-email`}
            type="email"
            required
            autoComplete="email"
            placeholder="seu@email.com.br"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={enviando}
            aria-invalid={erroDeEmail ? true : undefined}
            className="h-11 w-full rounded-auth-controle border border-auth-borda/15 bg-auth-superficie/[0.04] text-auth-texto placeholder:text-auth-texto-terciario px-3 text-sm transition-colors focus:border-auth-marca-hover focus:ring-2 focus:ring-auth-marca-hover/35 focus:outline-none disabled:opacity-60"
          />
          {erroDeEmail ? (
            <p className="text-auth-erro mt-1.5 text-xs">{erroDeEmail}</p>
          ) : null}
        </div>
        <button
          type="submit"
          disabled={enviando || !aceite}
          className="rounded-auth-controle bg-auth-marca-forte text-auth-marca-contraste hover:bg-auth-marca-forte-hover inline-flex h-11 shrink-0 items-center justify-center gap-2 px-6 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50"
        >
          {enviando ? (
            <Loader2 size={16} className="animate-spin" aria-hidden />
          ) : (
            <Mail size={16} aria-hidden />
          )}
          {enviando ? 'Enviando…' : 'Quero saber'}
        </button>
      </div>

      <label className="text-auth-texto-secundario mt-3 flex items-start gap-2 text-xs leading-relaxed">
        <input
          type="checkbox"
          checked={aceite}
          onChange={(e) => setAceite(e.target.checked)}
          disabled={enviando}
          className="border-auth-borda/25 text-auth-marca-forte focus:ring-auth-marca-hover/35 mt-0.5 size-4 shrink-0 rounded focus:ring-2"
        />
        <span>
          {TEXTO_CONSENTIMENTO_INTERESSE} Pode sair quando quiser, escrevendo para{' '}
          <a
            href="mailto:contato@specializer.com.br?subject=Sair%20da%20lista"
            className="text-auth-marca-hover hover:text-auth-acento underline-offset-2 hover:underline"
          >
            contato@specializer.com.br
          </a>
          .
        </span>
      </label>

      {erroGeral ? (
        <p role="alert" className="text-auth-erro mt-2 text-xs">
          {erroGeral.mensagem}
          {erroGeral.retryAfterSegundos
            ? ` Tente novamente em ${emMinutos(erroGeral.retryAfterSegundos)}.`
            : ''}
        </p>
      ) : null}
    </form>
  );
}
